import { getConversationByServerId } from "@mirage/service-conversation";
import { RoutePath } from "@mirage/webapp/routeTypes";
import { useEffect, useState } from "react";

const CONVERSATION_SEARCH_PARAM = "conversationId";
const SERVER_CONVERSATION_SEARCH_PARAM = "serverConversationId";

export function urlForConversationWithID(
  id: string,
  chatRoute: RoutePath.CHAT | RoutePath.EMBED_CHAT = RoutePath.CHAT,
) {
  return `${chatRoute}?${CONVERSATION_SEARCH_PARAM}=${id}`;
}

export function urlForConversationWithServerID(
  id: string,
  chatRoute: RoutePath.CHAT | RoutePath.EMBED_CHAT = RoutePath.CHAT,
) {
  return `${chatRoute}?${SERVER_CONVERSATION_SEARCH_PARAM}=${id}`;
}

export function parseConversationSearchParams(searchParams: URLSearchParams): {
  conversationId: string | undefined;
  serverConversationId: string | undefined;
} {
  return {
    conversationId: searchParams.get(CONVERSATION_SEARCH_PARAM) || undefined,
    serverConversationId:
      searchParams.get(SERVER_CONVERSATION_SEARCH_PARAM) || undefined,
  };
}

type ConversationIdResult =
  | {
      state: "loading";
      searchParams: URLSearchParams;
    }
  | {
      state: "loaded";
      conversationId: string | undefined; // undefined if no ID in search params
    };

/**
 * Hook to get the local conversation ID from the search params. This also handles translating
 * the server conversation ID to a local conversation ID if a server conversation ID is provided
 * instead.
 */
export function useConversationIdFromSearchParams(
  searchParams: URLSearchParams,
): ConversationIdResult {
  const [conversationId, setConversationId] = useState<ConversationIdResult>({
    state: "loading",
    searchParams,
  });
  useEffect(() => {
    // Reset to loading state when search params change
    setConversationId({ state: "loading", searchParams });

    getConversationIdFromSearchParams(searchParams)
      .then((conversationId) => {
        // Check if searchParams have changed since the request started
        setConversationId((prev) => {
          // Only update if this response is for the current searchParams
          if (prev.state === "loading" && prev.searchParams === searchParams) {
            return {
              state: "loaded",
              conversationId,
            };
          }
          // Otherwise keep existing state
          return prev;
        });
        return;
      })
      .catch((e) => {
        throw e;
      });
  }, [searchParams]);
  return conversationId;
}

async function getConversationIdFromSearchParams(
  searchParams: URLSearchParams,
): Promise<string | undefined> {
  const { conversationId, serverConversationId } =
    parseConversationSearchParams(searchParams);
  if (conversationId) {
    return conversationId;
  }
  if (serverConversationId) {
    const conversation = await getConversationByServerId(serverConversationId);
    return conversation.id;
  }
  return undefined;
}

// Workaround for a bug where there's a slash before query parameters
// on staging.dash.ai: /chat/?param=value -> /chat?param=value
export function normalizeUrl(url: string): string {
  return url.replace(/\/\?/g, "?");
}

/**
 * Small hook to normalize URLs on page load, specifically for the staging.dash.ai issue
 * where a slash gets inserted before query parameters on refresh
 */
export function useUrlNormalization() {
  useEffect(() => {
    const currentUrl = window.location.href;
    const hasSlashBeforeQuery = currentUrl.includes("/?");
    if (hasSlashBeforeQuery) {
      const normalizedUrl = currentUrl.replace(/\/\?/g, "?");
      window.history.replaceState(null, "", normalizedUrl);
    }
  }, []);
}
